import { useState } from 'react'
import { isEqual, isEmpty } from 'lodash-es'
import { Table } from 'semantic-ui-react'

import { Icon, Anchor, FlexRow, Help, Text } from '@/ui'
import { renderTitle } from '..'
import CheckButton from './CheckButton'
import TableHeaderFilter from './TableHeaderFilter'
import { HeaderCell } from './Styles'
import { TFunction } from 'i18next'
import { TableColumn } from '@/types/Table'

interface TableHeaderContentProps {
  tableName: string
  filters: any
  search: string
  sortable: boolean
  data: any[]
  columns: TableColumn[]
  sort: (key: string) => void
  sortKey: string
  sortDirection: string
  total: number
  onCheckAll: (ids: string[], uncheckAll?: boolean) => void
  t: TFunction
  isMobile: boolean
  graphileFilters: any
  invertFilter: (key: string, value: string) => void
  toggleFilterMode: (key: string) => void
  stickyHeader: boolean
  stickyHeaderNoOffset: boolean
  type: string
  addFilter: (key: string, value: string) => void
  removeFilter: (key: string, value: string) => void
}

const TableHeaderContent = ({
  tableName,
  filters,
  search,
  data,
  columns,
  sort,
  sortKey,
  sortDirection,
  total,
  onCheckAll,
  t,
  isMobile,
  graphileFilters,
  invertFilter,
  toggleFilterMode,
  stickyHeader,
  stickyHeaderNoOffset,
  addFilter,
  removeFilter,
}: TableHeaderContentProps) => {
  const [activeFilter, setActiveFilter] = useState<string | null>(null)

  const toggleFilter = (key?: string) => {
    if (activeFilter !== key) {
      setActiveFilter(key as string)
    } else {
      setActiveFilter(null)
    }
  }

  const addColumnFilter = (key: string, value: string) => {
    addFilter(key, value)
    toggleFilter()
  }

  const removeColumnFilter = (key: string, value: string) => {
    removeFilter(key, value)
    toggleFilter()
  }

  return (
    <Table.Row>
      {columns.length ? (
        columns.map((c, index) => {
          const isRightColumn =
            columns.length > 3 && index >= columns.length - 3

          const headingProps = {
            style: {
              width: c.checkbox ? '50px' : c.width || 'auto',
              position: 'relative',
            },
            textAlign: c.textAlign || 'left',
            stickyHeader,
            stickyHeaderNoOffset,
            zIndex: 9999 - index,
          }

          const parsedTitle = renderTitle(c.title ?? '', t)

          const title = c.checkbox ? (
            <CheckButton
              tableName={tableName}
              onCheckAll={onCheckAll}
              total={total}
              data={data}
              checkboxKey={c.key}
              onCheckAllQuery={c.onCheckAllQuery}
              queryVariables={{
                columns,
                sortKey,
                sortDirection,
                filters,
                search,
              }}
              graphileFilters={graphileFilters}
            />
          ) : (
            parsedTitle
          )

          let cellContent = isMobile ? '' : title

          if (c.sortable) {
            cellContent = (
              <Anchor
                onClick={() => sort(c.sortKey || c.key)}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  cursor: 'pointer',
                }}>
                {c.help ? (
                  <FlexRow>
                    <div>
                      <Text.Span light>{parsedTitle}</Text.Span>
                    </div>
                    <div>
                      <Help content={t(c.help)} />
                    </div>
                  </FlexRow>
                ) : (
                  <Text.Span
                    style={
                      typeof title === 'string'
                        ? {
                            whiteSpace:
                              title.split(' ').length === 1 && title.length < 20
                                ? 'nowrap'
                                : undefined,
                          }
                        : undefined
                    }
                    light>
                    {title}
                  </Text.Span>
                )}

                {!Array.isArray(sortKey) &&
                  sortKey === (c.sortKey || c.key) && (
                    <Icon name={`sort ${sortDirection}ending`} />
                  )}
                {Array.isArray(sortKey) && isEqual(sortKey, c.sortKey) && (
                  <Icon name={`sort ${sortDirection}ending`} />
                )}
              </Anchor>
            )
          }

          if (!isEmpty(c.filters)) {
            cellContent = (
              <TableHeaderFilter
                filters={filters}
                addFilter={addColumnFilter}
                toggleFilter={toggleFilter}
                invertFilter={invertFilter}
                toggleFilterMode={toggleFilterMode}
                removeFilter={removeColumnFilter}
                title={parsedTitle}
                activeFilter={activeFilter}
                sort={sort}
                sortDirection={sortDirection}
                sortKey={sortKey}
                isRightColumn={isRightColumn}
                c={c}
              />
            )
          }

          return (
            <HeaderCell key={c.key} {...headingProps}>
              <div>{cellContent}</div>
            </HeaderCell>
          )
        })
      ) : (
        <HeaderCell />
      )}
    </Table.Row>
  )
}

const TableHeader = (props: TableHeaderContentProps) => (
  <Table.Header>
    <TableHeaderContent {...props} />
  </Table.Header>
)

export default TableHeader
